/**
 * Class recipes of the HUMINT "field instrument" look. Import these instead of retyping the
 * strings so every screen keeps the same rhythm. Token classes only (see CLAUDE.md).
 *
 * Container variants: the shell root is `@container/humint`; `@3xl/humint:` = 768 px+,
 * `@5xl/humint:` = 1024 px+ (desktop). Outside the shell they fall back to the phone look.
 */
import type { MatchBand } from './types'

/** Uppercase micro-label: section headers, field captions, table heads. */
export const MICRO_LABEL =
  'text-2xs font-semibold uppercase tracking-[0.12em] text-muted-foreground'

/** Data readout: capture tags, times, coordinates, MGRS, scores, counts. */
export const MONO = 'font-mono tabular-nums'

/** Card surface: rounded-2xl on the phone, rounded-xl on the desktop. Add padding yourself. */
export const CARD =
  'rounded-2xl border border-border bg-card text-card-foreground @5xl/humint:rounded-xl'

/** Hairline section divider inside a card or panel. */
export const HAIRLINE = 'border-border border-t'

/** Visible focus for custom interactive elements (shadcn primitives bring their own). */
export const FOCUS_RING =
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background'

/** Visible focus for a row inside a card that clips its content: the ring is drawn inside the row. */
export const FOCUS_RING_INSET =
  'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset'

/** Tappable list row: >= 72 px on the phone, 64 px on the desktop. Selected = aria-current / aria-pressed. */
export const LIST_ROW = `flex min-h-18 w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left text-card-foreground transition-colors hover:bg-accent aria-[current=true]:border-primary aria-pressed:border-primary @5xl/humint:min-h-16 @5xl/humint:rounded-xl ${FOCUS_RING}`

/**
 * Text and icons on copper. White (`primary-foreground`) on copper is only 3.8:1, so use the
 * dark ink: foreground in light mode, background in dark mode (4.6:1 / 5.2:1).
 */
export const ON_PRIMARY = 'text-foreground dark:text-background'

/**
 * The one primary action of a screen, in the bottom thumb zone. Use with
 * <Button variant="solid" size="2xl" className={PRIMARY_THUMB}>: 56 px full width on the
 * phone, a 40 px inline button on the desktop.
 */
export const PRIMARY_THUMB = `w-full rounded-xl px-6 text-base font-semibold ${ON_PRIMARY} @5xl/humint:h-10 @5xl/humint:w-auto @5xl/humint:px-4 @5xl/humint:text-sm`

/** Secondary thumb button: <Button variant="outline" size="xl" className={SECONDARY_THUMB}>. */
export const SECONDARY_THUMB =
  'w-full rounded-xl text-base font-semibold @5xl/humint:h-10 @5xl/humint:w-auto @5xl/humint:px-4 @5xl/humint:text-sm'

/** Icon-only button target: 48 px on the phone, 40 px on the desktop. Always add aria-label. */
export const ICON_TARGET = 'size-12 rounded-xl @5xl/humint:size-10'

/**
 * The selected look of every selectable control: copper border + copper wash (+ weight on
 * toggles). Covers `aria-pressed` buttons, Radix toggles (`data-state=on`) and rows holding a
 * checked radio / checkbox. Never colour alone: pair it with a Check icon or a filled control.
 */
export const SELECTED =
  'aria-pressed:border-primary aria-pressed:bg-primary/15 aria-pressed:font-semibold data-[state=on]:border-primary data-[state=on]:bg-primary/15 data-[state=on]:font-semibold has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/15'

/** Caption text, 13 / 18 px: hints, meta lines, scope lines. */
export const TEXT_CAPTION = 'text-[13px] leading-[18px]'

/** Small body text, 15 px: chips, list secondary lines, dense phone copy. */
export const TEXT_BODY_SM = 'text-[15px]'

/** Card and sheet titles, 17 / 22 px. */
export const TEXT_TITLE = 'text-[17px] leading-[22px]'

/**
 * Selectable chip (48 px on the phone). Works on a plain <button aria-pressed> and on a
 * ToggleGroupItem (data-state=on); prefer `controls/ChipRadioGroup` / `controls/ToggleChips`,
 * which render the Check icon of the selected chip. Inside a modal panel (`@container/modal`)
 * it shrinks to 40 px from 768 px.
 */
export const CHIP = `inline-flex h-12 items-center gap-2 rounded-full border border-border bg-secondary px-4 ${TEXT_BODY_SM} font-medium whitespace-nowrap text-foreground transition-colors hover:bg-accent disabled:cursor-not-allowed disabled:border-dashed disabled:text-muted-foreground ${SELECTED} @5xl/humint:h-8 @5xl/humint:px-3 @5xl/humint:text-[13px] @3xl/modal:h-10 @3xl/modal:px-3.5 @3xl/modal:text-sm ${FOCUS_RING}`

/**
 * Full-width choice tile (56 px on the phone), e.g. encounter type or session kind in a
 * ToggleGroup. Add the justification yourself (`justify-start` / `justify-between`).
 */
export const TILE = `h-14 w-full gap-2.5 rounded-xl border border-border bg-secondary px-4 text-left text-base font-medium whitespace-normal text-foreground hover:bg-accent disabled:border-dashed disabled:opacity-100 disabled:text-muted-foreground ${SELECTED} @5xl/humint:h-11 @5xl/humint:text-sm`

/**
 * One segment of a segmented switch (ToggleGroupItem, 48 px on the phone): equal shares of the
 * track, never narrower than its label. Prefer `controls/SegmentedSwitch`, which is built on it.
 */
export const SEGMENT = `h-12 min-w-fit flex-1 gap-2 rounded-lg border border-transparent px-3 ${TEXT_BODY_SM} font-medium text-muted-foreground hover:bg-accent data-[state=on]:text-foreground ${SELECTED} @5xl/humint:h-9 @5xl/humint:text-sm`

/** A <label> row around a RadioGroupItem or Checkbox: the whole row is the target. */
export const OPTION_ROW = `flex min-h-12 w-full cursor-pointer items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-base font-normal leading-6 transition-colors hover:bg-accent ${SELECTED} @5xl/humint:min-h-10 @5xl/humint:py-2 @5xl/humint:text-sm`

/** Status pill base; prefer `<Pill tone=…>` from controls/Pill, which is built on it. */
export const PILL = 'h-6 gap-1.5 rounded-full border px-2.5 text-xs font-semibold'

export type PillTone = 'neutral' | 'muted' | 'warning' | 'destructive' | 'info' | 'copper'

export const PILL_TONE: Record<PillTone, string> = {
  neutral: 'border-border bg-secondary text-foreground',
  muted: 'border-transparent bg-accent text-muted-foreground',
  warning: 'border-warning-border bg-warning-surface text-warning-foreground',
  destructive: 'border-destructive-border bg-destructive-surface text-destructive-foreground',
  info: 'border-info-border bg-info-surface text-info-foreground',
  copper: 'border-transparent bg-primary/15 text-primary-strong',
}

/** Pill tone of a face or tattoo band. Never success/green: a match is a lead. */
export const BAND_TONE: Record<MatchBand, PillTone> = {
  strong: 'destructive',
  possible: 'warning',
  weak: 'muted',
}

/** Verdict banner surfaces: possible = warning, strong (dormant) = destructive, the rest muted. */
export const VERDICT_SURFACE = {
  possible: PILL_TONE.warning,
  strong: PILL_TONE.destructive,
  muted: 'border-border bg-card text-muted-foreground',
} as const

/** Inline warning callout (tag in use, not saved yet): icon + words on the warning surface. */
export const CALLOUT_WARNING = `flex items-start gap-2 rounded-xl border p-3 ${TEXT_CAPTION} font-medium ${PILL_TONE.warning}`

/** Faint dot-grid texture for camera, check and viewfinder surfaces. */
export const DOT_GRID =
  'bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px]'

/**
 * The four copper viewfinder brackets (face box, notice mark). Draw each as a span with
 * `absolute border-primary` plus its size, inside the box the brackets frame.
 */
export const BRACKET_CORNERS = [
  'top-0 left-0 rounded-tl-md border-t-2 border-l-2',
  'top-0 right-0 rounded-tr-md border-t-2 border-r-2',
  'bottom-0 left-0 rounded-bl-md border-b-2 border-l-2',
  'bottom-0 right-0 rounded-br-md border-b-2 border-r-2',
] as const

/** Entrance of sheets, docks and the verdict reveal. Motion only at high-impact moments. */
export const ENTER_UP = 'animate-in fade-in-0 slide-in-from-bottom-4 duration-300'

/** Pulsing "recording" dot; pair it with the word REC / "Nahráva sa". */
export const RECORDING_DOT = 'size-2 shrink-0 rounded-full bg-destructive animate-pulse'

/** Side insets of a surface that spans the screen: it stays clear of the notch in landscape. */
export const SAFE_AREA_X = 'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]'

/** Bottom thumb dock of a phone flow screen (safe-area aware). */
export const THUMB_DOCK =
  'flex shrink-0 flex-col gap-2 border-border border-t bg-background px-4 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] @5xl/humint:flex-row @5xl/humint:justify-end @5xl/humint:px-6 @5xl/humint:py-4'

// ── Modal panels ─────────────────────────────────────────────────────────────
// A sheet / dialog panel names its root `@container/modal` (it is portalled outside the shell,
// so the `/humint` variants never apply there); `@3xl/modal:` = the desktop dialog.

/** Bottom bar of a modal panel: stacked thumb buttons on the phone, a right-aligned row in the dialog. */
export const MODAL_FOOTER =
  'flex shrink-0 flex-col gap-2 border-border border-t px-4 pt-3 pb-[max(1.25rem,env(safe-area-inset-bottom))] @3xl/modal:flex-row @3xl/modal:items-center @3xl/modal:justify-end @3xl/modal:px-6 @3xl/modal:py-4'

/** The primary action of a modal panel: <Button variant="solid" size="2xl" className={MODAL_PRIMARY}>. */
export const MODAL_PRIMARY = `w-full rounded-xl px-6 text-base font-semibold ${ON_PRIMARY} @3xl/modal:h-10 @3xl/modal:w-auto @3xl/modal:px-4 @3xl/modal:text-sm`

/** Icon-only button in a modal panel header (close): 48 px, 40 px in the dialog. Add aria-label. */
export const MODAL_ICON_TARGET = 'size-12 shrink-0 rounded-xl @3xl/modal:size-10'
